import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import type Big from 'big.js';
import { get } from 'lodash-es';
import type { PricingModeEnum } from '../../../types';
import { isPricingModeRelative, toBig, type Leaves } from '../../../utils';
import { getSpreadPct } from '../../../utils/spread';
import { InlineFormattedNumber, NumberVariants } from '../../FormattedNumber';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

export interface SpreadColumnParams<T extends object = any> {
  /** Pass a bid and ask field, using which the column will compute the spread for you */
  bidField?: Leaves<T>;
  askField?: Leaves<T>;
  pricingModeField?: Leaves<T>;
  /** Defaults to true. Set to false to not show BPS as a suffix when it is relevant. */
  showBPSIfRelevant?: boolean;
  /** If set to true, will render the spread in red if the spread is negative (crossed bid-ask) */
  highlightNegativeSpread?: boolean;
}

type SpreadValue = Big | undefined;

/**
 * The spread column currently only supports showing relative (bps/%) spread, not absolute spread (in quote currency for example)
 *
 * Pass in a basic field if you have a pre-computed spread value (in percent, as in 1 = 1%)
 * Or, if you have a bid and ask value, pass in bid and ask fields via the params and the column will calculate your spread
 */
export const spread: ColDefFactory<Column<SpreadColumnParams>> = column => ({
  initialWidth: 80,
  ...baseColumn(column),
  headerClass: 'ag-center-aligned-cell',
  cellClass: 'ag-center-aligned-cell',
  valueGetter: ({ data }: ValueGetterParams<unknown>): SpreadValue => {
    if (column.field) {
      // column.field is used to pass in a field pointing at a pre-computed spread value. If provided, use it.
      return toBig(get(data, column.field));
    }

    // Else we continue, and below here we compute a spread based on passed bid and ask fields
    if (!column.params?.bidField || !column.params.askField) {
      return undefined;
    }

    const bid = get(data, column.params.bidField);
    const ask = get(data, column.params.askField);
    const pricingMode: PricingModeEnum | undefined = column.params.pricingModeField
      ? get(data, column.params.pricingModeField)
      : undefined;

    if (!bid || !ask) {
      return undefined;
    }

    return getSpreadPct(bid, ask, { isRelativePrice: isPricingModeRelative(pricingMode) });
  },

  valueFormatter: ({ value }: ValueFormatterParams<unknown, SpreadValue>) => {
    if (!value) {
      return '';
    }

    const { valueString, suffix } = getValueAndSuffixForRendering(value, column.params?.showBPSIfRelevant ?? true);
    return `${valueString} ${suffix ? suffix : ''}`;
  },
  cellRenderer: ({ value }: ICellRendererParams<unknown, SpreadValue>) => {
    if (!value) {
      return <></>;
    }

    const numberVariant =
      column.params?.highlightNegativeSpread && value.lt(0) ? NumberVariants.Negative : NumberVariants.Muted;

    const { valueString, suffix } = getValueAndSuffixForRendering(value, column.params?.showBPSIfRelevant ?? true);
    return <InlineFormattedNumber variant={numberVariant} number={valueString} currency={suffix} />;
  },
});

function getValueAndSuffixForRendering(
  value: Big,
  includeBPSIfApplicable: boolean
): { valueString: string; suffix: '%' | 'BPS' | undefined } {
  const renderInPct = value.abs().gt(10);
  if (renderInPct) {
    return { valueString: value.toFixed(2), suffix: '%' };
  } else {
    return { valueString: value.times(100).toFixed(2), suffix: includeBPSIfApplicable ? 'BPS' : undefined };
  }
}
